import { ApiError, describeError, FormError } from '@classgo/app-kit';
import { checkNit } from '@classgo/shared';
import { Button, Checkbox, Input } from '@classgo/ui';
import { type FormEvent, type ReactNode, useState } from 'react';

import { useT } from '../i18n';
import type { SchoolFormValues } from './types';

/**
 * A blank school.
 *
 * @returns The values.
 */
export const emptySchoolForm = (): SchoolFormValues => ({
  name: '',
  legalName: '',
  nit: '',
  daneCode: '',
  mainSedeName: 'Principal',
  isDemo: false,
});

/**
 * The school's identity, with the copy an onboarding rep needs under every field. `mode` decides
 * whether the main sede is asked (only at creation).
 */
export const SchoolForm = ({
  initial,
  mode,
  submitLabel,
  onSubmit,
  secondary,
}: {
  initial: SchoolFormValues;
  mode: 'create' | 'edit';
  submitLabel: string;
  onSubmit: (values: SchoolFormValues) => Promise<void>;
  secondary?: ReactNode;
}) => {
  const t = useT();
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nitError, setNitError] = useState<string | null>(null);
  const set = <K extends keyof SchoolFormValues>(key: K, value: SchoolFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const checked = values.nit.trim() ? checkNit(values.nit) : null;
    if (checked && 'error' in checked) {
      setNitError(checked.error);
      return;
    }
    setNitError(null);
    setBusy(true);
    try {
      await onSubmit(checked ? { ...values, nit: checked.nit } : values);
    } catch (thrown) {
      setError(
        thrown instanceof ApiError && thrown.issues.length > 0
          ? thrown.issues.map((issue) => `${issue.path}: ${issue.message}`).join(' · ')
          : describeError(t, thrown),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
      <FormError message={error} />
      <Input
        label={t('tenant.field.name')}
        help={t('tenant.field.name.help')}
        required
        minLength={2}
        maxLength={160}
        value={values.name}
        onChange={(event) => set('name', event.target.value)}
      />
      <Input
        label={t('tenant.field.legalName')}
        help={t('tenant.field.legalName.help')}
        maxLength={200}
        value={values.legalName}
        onChange={(event) => set('legalName', event.target.value)}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('tenant.field.nit')}
          help={t('tenant.field.nit.help')}
          {...(nitError ? { error: nitError } : {})}
          maxLength={32}
          value={values.nit}
          onChange={(event) => set('nit', event.target.value)}
        />
        <Input
          label={t('tenant.field.daneCode')}
          help={t('tenant.field.daneCode.help')}
          maxLength={32}
          value={values.daneCode}
          onChange={(event) => set('daneCode', event.target.value)}
        />
      </div>
      {mode === 'create' ? (
        <Input
          label={t('tenant.field.mainSedeName')}
          help={t('tenant.field.mainSedeName.help')}
          required
          maxLength={120}
          value={values.mainSedeName}
          onChange={(event) => set('mainSedeName', event.target.value)}
        />
      ) : null}
      <Checkbox
        label={t('tenant.field.isDemo')}
        description={t('tenant.field.isDemo.help')}
        checked={values.isDemo}
        onChange={(event) => set('isDemo', event.target.checked)}
      />
      <p className="text-xs text-gray-500">
        <span className="font-medium text-gray-700">{t('tenant.field.defaults')}</span>
        {' · '}
        {t('tenant.field.defaults.help')}
      </p>
      <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
        {secondary}
        <Button type="submit" loading={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
